/**
 * IntakeReadyBadge
 * ────────────────
 * Kleiner Header-Badge für Manager/Direktoren, der die Anzahl der aktuell
 * übergabe-reifen Intake-Projekte zeigt. Klick → springt in die Intake-
 * Pipeline mit voraktivem „Reif"-Filter.
 *
 * Sichtbar wird der Badge NUR wenn:
 *   • der User die Capability `see_intake_pipeline` hat (Vertriebsleiter+),
 *   • UND es mindestens 1 reifes Intake-Projekt gibt.
 *
 * Polling alle 60 s (plus Refresh bei Fenster-Fokus), belastet das Backend
 * durch das kompakte `/api/intake-pipeline/ready-count` kaum.
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Inbox } from "lucide-react";
import api from "@/lib/api";
import { useUserCaps } from "@/lib/useUserCaps";

const POLL_MS = 60_000;

export const IntakeReadyBadge = () => {
  const { hasCap, loaded } = useUserCaps();
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!loaded || !hasCap("see_intake_pipeline")) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/intake-pipeline/ready-count");
        if (alive) setCount(r.data?.ready || 0);
      } catch {
        /* stumm — Badge bleibt ohne Update */
      }
    };
    load();
    timerRef.current = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [loaded, hasCap]);

  if (!loaded || !hasCap("see_intake_pipeline") || count === 0) return null;

  return (
    <Link
      to="/pipeline/intake?ready=yes"
      data-testid="header-intake-ready-badge"
      title={`${count} Intake-Projekt${count === 1 ? "" : "e"} übergabe-reif — jetzt an den Vertrieb übergeben`}
      aria-label={`${count} übergabe-reife Intake-Projekte`}
      className="relative inline-flex items-center justify-center h-11 w-11 rounded-xl bg-emerald-500/12 hover:bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40 transition active:scale-95"
    >
      <Inbox size={18} strokeWidth={2.2} />
      <span
        data-testid="header-intake-ready-count"
        className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-[#09090B] text-[11px] font-black flex items-center justify-center leading-none ring-2 ring-[#09090B]"
      >
        {count > 99 ? "99+" : count}
      </span>
    </Link>
  );
};

export default IntakeReadyBadge;
